import type { RootState } from 'src/redux/store';
import type { UseFormSetValue } from 'react-hook-form';
import type { IContact } from 'src/pages/contacts/contacts.types';
import type { IAccount } from 'src/pages/accounts/accounts.types';

import { toast } from 'sonner';
import { useState, useEffect, useCallback } from 'react';

import { useAppSelector } from 'src/hook/hooks';
import { useGetMediaUrlMutation } from 'src/pages/chat/slice/chatApi';
import {
  useFilterTeamMutation,
  useGetTeamDtlMutation,
} from 'src/pages/setting/components/teams/slice/teamApi';
import {
  useGetUserDtlMutation,
  useFilterUserDetailMutation,
} from 'src/pages/setting/components/users/slice/userApi';

import {
  type ITicket,
  defaultFilter,
  type IDefaultFilter,
} from '../tickets.types';

interface IRecipient {
  id: string;
  name: string;
  avatarUrl?: string;
  ProfileURL?: string;
  isUser: boolean;
  assignedType: 'USER' | 'TEAM';
  assignedId: string;
}

export interface IsetQueryParamsHandler {
  type: 'search' | 'pagination' | 'reset';
  value?: string;
}

interface IUseGetAssigneeList {
  selectedTicket?: ITicket | IContact | IAccount | undefined;
  setValue?: UseFormSetValue<any>;
  singleFieldAssigee?: { assignedId: string; assignedType: string }; // Using for [Detail] single field assignee
  multiFieldAssign?: any;
  isDidPage?: boolean;
  isTeamPage?: boolean;
}

const updatedFilter = {
  ...defaultFilter,
  filters: [...defaultFilter.filters, { fieldname: 'status', search: 'ACTIVE' }],
};

const initialPaginationVal = { fetched: 0, total: Infinity };

export const useGetAssigneeList = ({
  selectedTicket,
  setValue,
  singleFieldAssigee,
  multiFieldAssign,
  isDidPage = false,
  isTeamPage = false,
}: IUseGetAssigneeList) => {
  const loginApiData = useAppSelector((state: RootState) => state.auth.loginApiData);

  const [filterUserList] = useFilterUserDetailMutation();
  const [filterTeam] = useFilterTeamMutation();
  const [getTeamDtl] = useGetTeamDtlMutation();
  const [getUserDetails] = useGetUserDtlMutation();
  const [getMediaUrl] = useGetMediaUrlMutation();

  const [_recordList, setRecordList] = useState<IRecipient[]>([]);
  const [userPagination, setUserPagination] = useState(initialPaginationVal);
  const [teamPagination, setTeamPagination] = useState(initialPaginationVal);
  const [queryParams, setQueryParams] = useState<IDefaultFilter>(updatedFilter);
  const [isFetching, setIsFetching] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedAssigneeDtl, setSelectedAssigneeDtl] = useState<any>(null);
  const [open, setOpen] = useState(false);

  const resetPagination = useCallback(() => {
    setUserPagination(initialPaginationVal);
    setTeamPagination(initialPaginationVal);
  }, []);

  const queryParamsHandler = useCallback(({ type, value }: IsetQueryParamsHandler) => {
    switch (type) {
      case 'search': {
        setQueryParams((prev) => {
          if (prev.globalSearch !== value) {
            resetPagination();
            return { ...prev, page: 1, globalSearch: value || '' };
          }
          return prev;
        });
        break;
      }
      case 'pagination':
        setQueryParams((prev) => ({ ...prev, page: prev.page + 1 }));
        break;
      case 'reset':
        setQueryParams(updatedFilter);
        break;
      default:
        break;
    }
  }, [resetPagination]);

  const openListHandler = useCallback(() => setOpen(true), []);
  const closeListHandler = useCallback(() => {
    setOpen(false);
    queryParamsHandler({ type: 'reset' });
  }, [queryParamsHandler]);

  const selectedAssigneeHandler = useCallback((selectedAssignee: any) => {
    setSelectedAssigneeDtl(selectedAssignee);
  }, []);

  const searchValueHandler = useCallback((value: string) => setSearchValue(value), []);

  const listScrollHandler = useCallback((event: React.UIEvent<HTMLUListElement>) => {
    const target = event.currentTarget;
    const isBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 5;

    const userHasMore = userPagination.fetched < userPagination.total;
    const teamHasMore = teamPagination.fetched < teamPagination.total;

    if (isBottom && !isFetching && (userHasMore || teamHasMore)) {
      queryParamsHandler({ type: 'pagination' });
    }
  }, [userPagination, teamPagination, isFetching, queryParamsHandler]);

  const getProfileUrl = async (filePath: string | null) => {
    if (filePath) {
      const { data: uploadFileUrl } = await getMediaUrl({ fileName: filePath });
      return uploadFileUrl;
    }
    return '';
  };

  const transformRecord = useCallback(
    async (item: any) => {
      if (!item) return item;

      // Check if it's a join record (ITicketAssign) or a flat record
      const isJointRecord = !!item.assignedId && (!!item.assignedUser || !!item.assignedTeam);
      const record = isJointRecord ? (item.assignedUser || item.assignedTeam) : item;

      // Determine type
      const assignedType = item.assignedType || (record.users ? 'TEAM' : 'USER');
      const isUser = assignedType === 'USER';

      // Unified name resolution
      const name = isUser
        ? `${record.firstName ?? ''} ${record.lastName ?? ''}`.trim() ||
          record.email ||
          record.phone ||
          ''
        : record.teamName ?? record.name ?? '';

      // ID should be the actual user/team ID
      const id = isJointRecord ? item.assignedId : item.id;

      const profileSource = record?.ProfileURL ?? record?.avatarUrl ?? '';
      const url =
        profileSource &&
        typeof profileSource === 'string' &&
        (profileSource.startsWith('http') ||
          profileSource.startsWith('blob:') ||
          profileSource.startsWith('data:'))
          ? profileSource
          : await getProfileUrl(profileSource);

      return {
        ...record,
        id,
        name,
        isUser,
        assignedType,
        assignedId: id,
        avatarUrl: url,
        ProfileURL: url,
      } as IRecipient;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getMediaUrl]
  );

  const getAssigneeListFunc = useCallback(
    async (params: IDefaultFilter) => {
      try {
        setIsFetching(true);

        const promises = [];

        const getRecordList = async (list = []) => {
          if (list && list.length > 0) {
            const updatedList = list.map(transformRecord);
            return await Promise.all(updatedList);
          }
          return [];
        };

        if (userPagination.fetched < userPagination.total && !isDidPage) {
          promises.push(
            filterUserList(params)
              .unwrap()
              .then(async (res: any) => {
                setUserPagination({
                  fetched: userPagination.fetched + res.list.length,
                  total: res.total,
                });
                return { list: await getRecordList(res.list ?? []), total: res.total };
              })
              .catch((err: any) => {
                console.error('User list fetch failed:', err);
                return { list: [], total: 0 };
              })
          );
        } else {
          promises.push(Promise.resolve({ list: [], total: 0 }));
        }

        if (teamPagination.fetched < teamPagination.total && !isTeamPage) {
          promises.push(
            filterTeam(params)
              .unwrap()
              .then(async (res: any) => {
                setTeamPagination({
                  fetched: teamPagination.fetched + res.list.length,
                  total: res.total,
                });
                return {
                  list: await getRecordList(res.list ?? []),
                  total: res.total,
                };
              })
              .catch((err: any) => {
                console.error('Team list fetch failed:', err);
                return { list: [], total: 0 };
              })
          );
        } else {
          promises.push(Promise.resolve({ list: [], total: 0 }));
        }

        const [userList, teamList] = await Promise.all(promises);

        const combinedList = [...userList.list, ...teamList.list];

        const filterAssignee = (list: any[]) => {
          if (!selectedAssigneeDtl) return list;
          const selectedIds = Array.isArray(selectedAssigneeDtl)
            ? selectedAssigneeDtl.map((item: any) => item.id)
            : [selectedAssigneeDtl.id];
          return list.filter((item) => !selectedIds.includes(item.id));
        };

        const filteredList = filterAssignee(combinedList);

        // setRecordList((prev: any) =>
        //   params.globalSearch ? [...combinedList] : [...prev, ...filteredList]
        // );

        setRecordList((prev: any) => {
          const newList = params.globalSearch ? filteredList : [...prev, ...filteredList];
          const seen = new Set();
          return newList.filter((item) => {
            const isDup = seen.has(item.id);
            seen.add(item.id);
            return !isDup;
          });
        });
      } catch (error) {
        setIsFetching(false);
        console.error('Error fetching list:', error);
        toast.error('List Fetching Failed');
      } finally {
        setIsFetching(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedAssigneeDtl, userPagination, teamPagination]
  );

  const getAssignedDetails = useCallback(
    (assignedId: string, assignedType: string) => {
      if (!assignedId && !assignedType) return;
      if (loginApiData && 'id' in loginApiData && loginApiData.id) {
        try {
          if (assignedType === 'USER') {
            getUserDetails(assignedId).then(async (res: any) => {
              if (res && res?.data) {
                const updatedResp = await transformRecord(res?.data);
                setSelectedAssigneeDtl(updatedResp);
                if (setValue) (setValue as UseFormSetValue<any>)('assignUsers', [updatedResp]);
              }
            });
          } else {
            getTeamDtl({ id: assignedId }).then(async (res: any) => {
              if (res && res?.data) {
                const updatedResp = await transformRecord(res?.data);
                setSelectedAssigneeDtl(updatedResp);
                if (setValue) (setValue as UseFormSetValue<any>)('assignUsers', [updatedResp]);
              }
            });
          }
        } catch (error) {
          toast.error('Record Detail Fetching Failed');
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [transformRecord]
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      queryParamsHandler({ type: 'search', value: searchValue });
    }, 500);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  useEffect(() => {
    if (open) {
      getAssigneeListFunc(queryParams);
    } else {
      let updatedList = [];

      if (selectedAssigneeDtl) {
        if (multiFieldAssign) {
          // Assume selectedAssigneeDtl is already an array or compatible format
          updatedList = Array.isArray(selectedAssigneeDtl)
            ? selectedAssigneeDtl
            : [selectedAssigneeDtl];
        } else {
          // Only one item allowed
          updatedList = [selectedAssigneeDtl];
        }
      }

      setRecordList(updatedList);
      resetPagination();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, queryParams, selectedAssigneeDtl]);

  // This useEffect only runs when the user open update ticket modal
  // useEffect(() => {
  //   if (selectedTicket && selectedTicket?.assignedId && selectedTicket?.assignedType) {
  //     getAssignedDetails(selectedTicket?.assignedId, selectedTicket?.assignedType?.toUpperCase());
  //   }
  //   return () => {
  //     setSelectedAssigneeDtl(null);
  //   };
  // }, [selectedTicket]);

  useEffect(() => {
    if (singleFieldAssigee) {
      getAssignedDetails(singleFieldAssigee?.assignedId, singleFieldAssigee?.assignedType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleFieldAssigee?.assignedId]);

  const handleResetSelectedAssignee = useCallback((data: any) => {
    if (setValue) (setValue as UseFormSetValue<any>)('assignUsers', data);
    // getAssignedDetails(assignedId, assignedType);
  }, [setValue]);

  useEffect(() => {
    if (multiFieldAssign) {
      const resolveMultiField = async () => {
        if (Array.isArray(multiFieldAssign)) {
          const resolved = await Promise.all(multiFieldAssign.map(transformRecord));
          setSelectedAssigneeDtl(resolved);
          if (setValue) (setValue as UseFormSetValue<any>)('assignUsers', resolved);
        } else {
          const resolved = await transformRecord(multiFieldAssign);
          setSelectedAssigneeDtl(resolved);
          if (setValue) (setValue as UseFormSetValue<any>)('assignUsers', [resolved]);
        }
      };
      resolveMultiField();
    } else if (!singleFieldAssigee) {
      setSelectedAssigneeDtl(null);
    }
  }, [multiFieldAssign, singleFieldAssigee, transformRecord, setValue]);

  return {
    _recordList,
    isFetching,
    searchValue,
    open,
    resetPagination,
    queryParamsHandler,
    listScrollHandler,
    searchValueHandler,
    openListHandler,
    closeListHandler,
    selectedAssigneeHandler,
    handleResetSelectedAssignee,
    selectedAssigneeDtl,
  };
};
